import { MESSAGES_LIMIT } from "constants/messages";

import { formatValidQueryParamUrlCommaSeparated } from "../utils/common";
import { formatMessage } from "../utils/messages.util";
import { emptySplitApi } from "./empty-split-api";
import type { PromptOutputFormat } from "./types/prompts.type";
import {
  AssigneeMessageInput,
  Message,
  MessageFilters,
  MessageInput,
  MessageResponse,
  ReadMessageInput
} from "./types/messages.type";

export const extendedMessagesApi = emptySplitApi.injectEndpoints({
  overrideExisting: true,
  endpoints: (builder) => ({
    getMessages: builder.query<Message[], MessageFilters>({
      query: ({
        patient,
        offset = 0,
        limit = MESSAGES_LIMIT,
        query = "{*}",
        ...params
      }) =>
        `/messages/?patient=${patient}&limit=${limit}&offset=${offset}&${formatValidQueryParamUrlCommaSeparated(
          params
        )}&ordering=-created_at,-id&query=${query}`,
      providesTags: ["Messages"],
      transformResponse: (response: MessageResponse[]) =>
        response.map(formatMessage)
    }),
    addMessage: builder.mutation<Message, MessageInput>({
      query: (messageData) => ({
        url: "/messages/",
        method: "POST",
        body: messageData
      }),
      invalidatesTags: ["Messages", "UnreadMessagesCount"]
    }),
    updateMessageStatus: builder.mutation<Message, ReadMessageInput>({
      query: ({ id, is_read }) => ({
        url: `/messages/${id}/`,
        method: "PATCH",
        body: { is_read }
      }),
      transformResponse: formatMessage,
      invalidatesTags: ["Messages", "UnreadMessagesCount"]
    }),
    updateMessageAssignee: builder.mutation<Message, AssigneeMessageInput>({
      query: ({ id, assignee }) => ({
        url: `/messages/${id}/`,
        method: "PATCH",
        body: { assignee }
      }),
      transformResponse: formatMessage,
      invalidatesTags: ["Messages", "UnreadMessagesCount"]
    }),
    autoCompleteMessage: builder.mutation<
      string,
      { message_id?: number; text?: string; clinic_id?: number; format?: PromptOutputFormat }
    >({
      query: (messageData) => ({
        url: "/messages/complete",
        method: "POST",
        body: messageData
      })
    }),
    readAllPatientMessages: builder.mutation<void, number>({
      query: (patietId) => ({
        url: `/patients/${patietId}/messages/read/`,
        method: "PATCH"
      }),
      invalidatesTags: ["Messages", "UnreadMessagesCount"]
    }),
    readAllMessagesGlobal: builder.mutation<void, void>({
      query: () => ({
        url: "/patients/messages/read_all/",
        method: "PATCH"
      }),
      invalidatesTags: ["Messages", "UnreadMessagesCount"]
    }),
    getUnreadMessagesCount: builder.query<
      number,
      { clinicId?: number; clinics?: number[] }
    >({
      query: ({ clinicId, clinics }) =>
        `messages/unread-messages-count/?${formatValidQueryParamUrlCommaSeparated(
          { clinic: clinicId, clinics }
        )}`,
      providesTags: ["UnreadMessagesCount"]
    })
  })
});

export const {
  useGetMessagesQuery,
  useAddMessageMutation,
  useUpdateMessageStatusMutation,
  useUpdateMessageAssigneeMutation,
  useAutoCompleteMessageMutation,
  useReadAllPatientMessagesMutation,
  useReadAllMessagesGlobalMutation,
  useGetUnreadMessagesCountQuery
} = extendedMessagesApi;
